export const maxSpreadsheetPreviewBytes = 20 * 1024 * 1024;
const maxSheets = 12;
const maxRows = 500;
const maxColumns = 100;

export interface SpreadsheetPreviewSheet {
  name: string;
  rows: string[][];
  columnCount: number;
  truncated: boolean;
}

export interface SpreadsheetPreviewWorkbook {
  sheets: SpreadsheetPreviewSheet[];
  truncated: boolean;
}

type PreviewableAttachment = { mimeType: string; originalName: string };

export function isPdfAttachment(attachment: PreviewableAttachment): boolean {
  return (
    attachment.mimeType === 'application/pdf' ||
    attachment.originalName.toLowerCase().endsWith('.pdf')
  );
}

export function isSpreadsheetAttachment(attachment: PreviewableAttachment): boolean {
  return (
    attachment.mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    attachment.originalName.toLowerCase().endsWith('.xlsx')
  );
}

export function normalizeSpreadsheetWorkbook(
  source: ReadonlyArray<{ name: string; data: ReadonlyArray<ReadonlyArray<unknown>> }>,
): SpreadsheetPreviewWorkbook {
  const sheets = source.slice(0, maxSheets).map((sheet) => {
    const sourceRows = sheet.data.slice(0, maxRows);
    const columnCount = Math.min(
      maxColumns,
      sourceRows.reduce((largest, row) => Math.max(largest, row.length), 0),
    );
    const rows = sourceRows.map((row) =>
      row.slice(0, maxColumns).map((cell) => formatSpreadsheetCell(cell)),
    );
    return {
      name: sheet.name.trim() || 'Лист',
      rows,
      columnCount,
      truncated: sheet.data.length > maxRows || sheet.data.some((row) => row.length > maxColumns),
    };
  });
  return {
    sheets,
    truncated: source.length > maxSheets || sheets.some((sheet) => sheet.truncated),
  };
}

export function spreadsheetColumnLabel(index: number): string {
  let value = index + 1;
  let label = '';
  while (value > 0) {
    value -= 1;
    label = String.fromCharCode(65 + (value % 26)) + label;
    value = Math.floor(value / 26);
  }
  return label;
}

function formatSpreadsheetCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return new Intl.DateTimeFormat('ru-RU').format(value);
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}
